// Outside Services Transfer - Fabric -- overview (zeenat_pret only).
//
// Fabric sent to outside vendors; every document raised an Issue Note. Same
// family as the other redesigned overviews (ShortCutComponents/overviewScreen.css):
// header card, filter row with search, one server-side pager, busy overlay
// instead of a full-page loader.
//
// Amount is the vendor's service amount (qty x rate); hovering it shows the
// issue's value at average rate (agreed 12-Oct-26). Status reads from the
// receipts: Pending / Partly Received / Received / Closed.
import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Button, Input, Spinner } from "reactstrap";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import { baseurl } from "../../../baseurl";
import { checkPermission } from "../../../Helper";
import { openOspFabricIssuePdf } from "./ospFabricIssuePrint";
import "./formsTable.css";
import "../ShortCutComponents/overviewScreen.css";
import "./ospFabricIssue.css";
import AttachmentViewModal from "../ShortCutComponents/AttachmentViewModal";
import { GrAttachment } from "react-icons/gr";

const token = localStorage.getItem("token");
const username = localStorage.getItem("username");
const companyId = localStorage.getItem("companyId");
const companySymbolCurrency = localStorage.getItem("companySymbolCurrency") || "";

// Every rights check on this module is keyed to this one path, including the
// form's Add / Edit, so a single rights row governs the whole module. One
// rights call per action: View on load, Delete when Delete is clicked, Add /
// Edit by the form when it opens.
const PAGE_PATH = "/ospFabricIssueOverview";
const FORM_PATH = "/ospFabricIssueForm";
const PAGE_SIZE = Number(localStorage.getItem("page_size")) || 30;
const headers = { headers: { authorization: token } };

// Receipt actions on each row (agreed 12-Oct-26): Receive / Receipts are
// checked against the receipt screens' own rights row, Close against Edit on
// this one.
const RECEIPT_PATH = "/ospFabricReceiptOverview";
const RECEIPT_FORM_PATH = "/ospFabricReceiptForm";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "partly", label: "Partly Received" },
  { value: "received", label: "Received" },
  { value: "closed", label: "Closed" },
];

const money = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
const qtyFmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 3 });

const hasAttachment = (raw) => {
  if (!raw) return false;
  const v = raw.toString().trim();
  if (v === "" || v === "[]" || v === "null") return false;
  if (v.startsWith("[") && v.endsWith("]")) {
    try {
      const parsed = JSON.parse(v);
      return Array.isArray(parsed) && parsed.some((f) => f && String(f).trim() !== "");
    } catch (e) {
      return true;
    }
  }
  return true;
};

export default function OspFabricIssueOverview(props) {
  const [vendors, setVendors] = useState([]);
  const [articles, setArticles] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);

  // Criteria as typed vs as last searched: the list only reloads on Search /
  // Enter / page, never while the user is still typing.
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [article, setArticle] = useState(null);
  const [vendor, setVendor] = useState(null);
  const [workType, setWorkType] = useState(null);
  const [status, setStatus] = useState(null);
  const [searchDraft, setSearchDraft] = useState("");
  const [criteria, setCriteria] = useState({});

  const [page, setPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [showAttachmentModal, setShowAttachmentModal] = useState(false);
  const [selectedRowDetails, setSelectedRowDetails] = useState(null);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    (async () => {
      const denied = await checkPermission(PAGE_PATH, 0);
      if (denied) return;
      axios
        .post(`${baseurl}/lovs/getAllSupplier`, { companyId, username }, headers)
        .then((res) =>
          setVendors(
            (res.data.data || []).map((s) => ({ id: s.id, value: s.id, label: s.label })),
          ),
        )
        .catch(() => setVendors([]));
      axios
        .post(`${baseurl}/ospFabIssue/getFilterLists`, { companyId, username }, headers)
        .then((res) => {
          setArticles(res.data.articles || []);
          setWorkTypes(res.data.workTypes || []);
        })
        .catch(() => {});
      load({}, 1, false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = async (crit, nextPage, all) => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${baseurl}/ospFabIssue/getOverview`,
        {
          companyId,
          username,
          ...crit,
          offSet: (nextPage - 1) * PAGE_SIZE,
          pageSize: PAGE_SIZE,
          allData: all,
        },
        headers,
      );
      setRows(res.data.data || []);
      setTotal(Number(res.data.totalCount) || 0);
      setCriteria(crit);
      setPage(nextPage);
      setShowAll(all);
    } catch (error) {
      Swal.fire(
        "Error",
        (error.response && error.response.data && error.response.data.message) ||
          "Could not load the documents.",
        "error",
      );
    } finally {
      setBusy(false);
      setFirstLoad(false);
    }
  };

  const runSearch = () =>
    load(
      {
        fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : null,
        toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : null,
        articleId: article ? article.id : null,
        supplierId: vendor ? vendor.id : null,
        servTypeId: workType ? workType.id : null,
        status: status ? status.value : null,
        searchField: searchDraft.trim(),
      },
      1,
      false,
    );

  const goPage = (p) => load(criteria, Math.min(Math.max(1, p), totalPages), false);

  // ---- Row actions ----
  const openForm = (row, mode) =>
    // Same contract as /CreateBill: updateCheck 0 = edit, 2 = preview.
    props.history.push({
      pathname: FORM_PATH,
      state: {
        id: row.id,
        updateCheck: mode === "edit" ? 0 : 2,
        goBackUrl: PAGE_PATH,
      },
    });

  const printRow = async (row) => {
    // Opened now, filled after the fetch -- a window.open after an await is
    // treated as a popup and blocked.
    const win = window.open("", "_blank");
    try {
      const res = await axios.post(
        `${baseurl}/ospFabIssue/getById`,
        { id: row.id, companyId, username },
        headers,
      );
      openOspFabricIssuePdf(res.data, win);
    } catch (error) {
      if (win) win.close();
      Swal.fire("Error", "Could not load the document for printing.", "error");
    }
  };

  const deleteRow = async (row) => {
    const denied = await checkPermission(PAGE_PATH, 3);
    if (denied) return;
    const confirm = await Swal.fire({
      title: `Delete Outside Services Transfer # ${row.doc_no}?`,
      text: "Its Issue Note and journal voucher will be removed too. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete it",
    });
    if (!confirm.isConfirmed) return;
    setBusy(true);
    try {
      await axios.post(
        `${baseurl}/ospFabIssue/delete`,
        { id: row.id, companyId, username },
        headers,
      );
      Swal.fire("Deleted!", "Your Record has been deleted.", "success");
      // Stay on the same page unless it just emptied.
      const nextPage = rows.length === 1 && page > 1 ? page - 1 : page;
      load(criteria, nextPage, showAll);
    } catch (error) {
      setBusy(false);
      Swal.fire(
        "Not deleted",
        (error.response && error.response.data && error.response.data.message) ||
          "Something went wrong. Please try again.",
        "error",
      );
    }
  };

  const receiveRow = async (row) => {
    const denied = await checkPermission(RECEIPT_PATH, 1);
    if (denied) return;
    props.history.push({
      pathname: RECEIPT_FORM_PATH,
      state: { issueId: row.id, goBackUrl: PAGE_PATH },
    });
  };

  // The receipt overview checks its own View right on load.
  const receiptsRow = (row) =>
    props.history.push({
      pathname: RECEIPT_PATH,
      state: { issueId: row.id, transferNo: row.doc_no },
    });

  const closeRow = async (row) => {
    const denied = await checkPermission(PAGE_PATH, 2);
    if (denied) return;
    const confirm = await Swal.fire({
      title: `Close transfer # ${row.doc_no}?`,
      text: "What has not been received stays recorded as a shortfall and no more receipts can be entered. Only the Wizmen team can reopen it.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, close it",
    });
    if (!confirm.isConfirmed) return;
    setBusy(true);
    try {
      await axios.post(`${baseurl}/ospFabIssue/close`, { id: row.id, companyId, username }, headers);
      Swal.fire("Closed", `Transfer # ${row.doc_no} is closed.`, "success");
      load(criteria, page, showAll);
    } catch (error) {
      setBusy(false);
      Swal.fire(
        "Not closed",
        (error.response && error.response.data && error.response.data.message) ||
          "Something went wrong. Please try again.",
        "error",
      );
    }
  };

  // ---- Formatters ----
  const docLink = (cell, row) => (
    <span className="ocv-link" title="Preview" onClick={() => openForm(row, "view")}>
      {cell}
    </span>
  );
  const dateFmt = (cell) => (cell ? moment(cell).format("DD-MMM-YYYY hh:mm A") : "");
  const textFmt = (cell) => <span title={cell || ""}>{cell || ""}</span>;
  const qtyCell = (cell) => <span className="osp-num">{qtyFmt(cell)}</span>;
  const amountFmt = (cell, row) => (
    <span
      className="osp-num osp-hover"
      title={`Issue value (average rate): ${companySymbolCurrency} ${money(row.issue_amount)}${
        row.issue_no ? ` -- Issue Note # ${row.issue_no}` : ""
      }`}
    >
      {companySymbolCurrency} {money(cell)}
    </span>
  );
  const actionsFmt = (cell, row) => {
    const locked = Boolean(row.locked_reason);
    const canReceive = row.status === "Pending" || row.status === "Partly Received";
    return (
      <div className="ocv-row-actions">
        {canReceive && (
          <Button size="sm" color="success" title="Receive" disabled={busy} onClick={() => receiveRow(row)}>
            <i className="fa fa-download"></i>
          </Button>
        )}
        {Number(row.receipt_count) > 0 && (
          <Button size="sm" outline color="info" title={`Receipts (${row.receipt_count})`} onClick={() => receiptsRow(row)}>
            <i className="fa fa-list"></i>
          </Button>
        )}
        {row.status === "Partly Received" && (
          <Button size="sm" outline color="warning" title="Close" disabled={busy} onClick={() => closeRow(row)}>
            <i className="fa fa-lock"></i>
          </Button>
        )}
        <Button size="sm" outline color="secondary" title="Preview" onClick={() => openForm(row, "view")}>
          <i className="fa fa-eye"></i>
        </Button>
        <Button size="sm" outline color="secondary" title="Print" onClick={() => printRow(row)}>
          <i className="fa fa-print"></i>
        </Button>
        {hasAttachment(row.attachment_file) && (
          <Button
            size="sm"
            outline
            color="primary"
            title="View Attachment"
            onClick={() => {
              setSelectedRowDetails(row);
              setShowAttachmentModal(true);
            }}
          >
            <GrAttachment />
          </Button>
        )}
        <Button
          size="sm"
          color="primary"
          title={row.locked_reason || "Edit"}
          disabled={busy || locked}
          onClick={() => openForm(row, "edit")}
        >
          <i className="fa fa-pencil"></i>
        </Button>
        <Button
          size="sm"
          color="danger"
          title={row.locked_reason || "Delete"}
          disabled={busy || locked}
          onClick={() => deleteRow(row)}
        >
          <i className="fa fa-trash"></i>
        </Button>
      </div>
    );
  };

  // ---- Pager (server side) ----
  const renderPager = () => {
    const atStart = busy || page <= 1 || showAll;
    const atEnd = busy || page >= totalPages || showAll;
    const start = Math.max(1, Math.min(page - 1, totalPages - 2));
    const pages = [];
    for (let i = start; i <= Math.min(totalPages, start + 2); i++) pages.push(i);
    const shown = showAll
      ? rows.length
      : rows.length
        ? `${(page - 1) * PAGE_SIZE + 1} - ${(page - 1) * PAGE_SIZE + rows.length}`
        : 0;
    return (
      <div className="ocv-tab-toolbar">
        <div className="ocv-count">
          Showing {shown} of {total}
        </div>
        <div className="ocv-pager btn-group btn-group-sm" role="group">
          <button type="button" className="btn btn-light" disabled={atStart} onClick={() => goPage(1)} title="First page">
            <i className="fa fa-angle-double-left"></i>
          </button>
          <button type="button" className="btn btn-light" disabled={atStart} onClick={() => goPage(page - 1)} title="Previous page">
            <i className="fa fa-angle-left"></i>
          </button>
          {pages.map((p) => (
            <button
              key={p}
              type="button"
              className={"btn " + (p === page && !showAll ? "btn-primary" : "btn-light")}
              disabled={busy}
              onClick={() => goPage(p)}
            >
              {p}
            </button>
          ))}
          <button type="button" className="btn btn-light" disabled={atEnd} onClick={() => goPage(page + 1)} title="Next page">
            <i className="fa fa-angle-right"></i>
          </button>
          <button type="button" className="btn btn-light" disabled={atEnd} onClick={() => goPage(totalPages)} title="Last page">
            <i className="fa fa-angle-double-right"></i>
          </button>
          <button
            type="button"
            className={"btn " + (showAll ? "btn-primary" : "btn-light")}
            disabled={busy}
            onClick={() => load(criteria, 1, true)}
            title="Show every record of the current selection"
          >
            All
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="animated fadeIn ocv-page">
      <div className="ocv-card">
        <div className="ocv-header">
          <div>
            <h4 className="ocv-title">Outside Services Transfer - Fabric</h4>
            <div className="ocv-subtitle">
              Fabric sent to outside vendors for dyeing, printing and embroidery
            </div>
          </div>
          <div className="ocv-actions">
            <Button
              color="primary"
              onClick={() =>
                props.history.push({ pathname: FORM_PATH, state: { goBackUrl: PAGE_PATH } })
              }
            >
              <i className="fa fa-plus mr-2"></i>New Transfer
            </Button>
          </div>
        </div>

        <div className="ocv-filters ocv-filters-inline">
          <div className="ocv-filter-field osp-field">
            <label>From</label>
            <DatePicker
              selected={fromDate}
              onChange={setFromDate}
              dateFormat="dd-MMM-yyyy"
              isClearable
              disabled={busy}
              placeholderText="Any date"
            />
          </div>
          <div className="ocv-filter-field osp-field">
            <label>To</label>
            <DatePicker
              selected={toDate}
              onChange={setToDate}
              dateFormat="dd-MMM-yyyy"
              isClearable
              disabled={busy}
              placeholderText="Any date"
            />
          </div>
          <div className="ocv-filter-field">
            <label>Article</label>
            <Select
              isClearable
              value={article}
              options={articles}
              onChange={(v) => setArticle(v || null)}
              isDisabled={busy}
              placeholder="All articles"
            />
          </div>
          <div className="ocv-filter-field">
            <label>Vendor</label>
            <Select
              isClearable
              value={vendor}
              options={vendors}
              onChange={(v) => setVendor(v || null)}
              isDisabled={busy}
              placeholder="All vendors"
            />
          </div>
          <div className="ocv-filter-field">
            <label>Work Type</label>
            <Select
              isClearable
              value={workType}
              options={workTypes}
              onChange={(v) => setWorkType(v || null)}
              isDisabled={busy}
              placeholder="All"
            />
          </div>
          <div className="ocv-filter-field">
            <label>Status</label>
            <Select
              isClearable
              value={status}
              options={STATUS_OPTIONS}
              onChange={(v) => setStatus(v || null)}
              isDisabled={busy}
              placeholder="All"
            />
          </div>
          <div className="ocv-filter-search">
            <div className="ocv-search">
              <i className="fa fa-search"></i>
              <Input
                type="text"
                placeholder="Doc no, article, vendor, work type…"
                value={searchDraft}
                disabled={busy}
                onChange={(e) => setSearchDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
              />
              {searchDraft ? (
                <button
                  type="button"
                  className="ocv-search-clear"
                  disabled={busy}
                  title="Clear search"
                  // Only clears the box; the list reloads on Search / Enter.
                  onClick={() => setSearchDraft("")}
                >
                  ×
                </button>
              ) : null}
            </div>
            <Button color="primary" onClick={runSearch} disabled={busy}>
              <i className="fa fa-search mr-2"></i>Search
            </Button>
          </div>
        </div>

        {renderPager()}

        <div className={"ocv-table" + (busy && !firstLoad ? " ocv-busy" : "")} translate="no">
          {firstLoad ? (
            <div className="ocv-loading">
              <Spinner size="sm" color="primary" className="mr-2" />
              Loading…
            </div>
          ) : (
            <>
              {busy && <div className="ocv-busy-note">Loading…</div>}
              <BootstrapTable
                data={rows}
                striped
                hover
                tableHeaderClass="rpr_header"
                tableBodyClass="rpr_body"
                containerClass="rpr_container"
                options={{ noDataText: "No documents for this selection" }}
              >
                <TableHeaderColumn dataField="doc_date" dataFormat={dateFmt} width="150">
                  DATE
                </TableHeaderColumn>
                <TableHeaderColumn dataField="doc_no" dataFormat={docLink} width="80">
                  DOC NO
                </TableHeaderColumn>
                <TableHeaderColumn dataField="article_label" dataFormat={textFmt} width="200">
                  ARTICLE
                </TableHeaderColumn>
                <TableHeaderColumn dataField="supplier_name" dataFormat={textFmt} width="180">
                  VENDOR
                </TableHeaderColumn>
                <TableHeaderColumn dataField="serv_type_desc" dataFormat={textFmt} width="130">
                  WORK TYPE
                </TableHeaderColumn>
                {/* Embroidery lines' components, comma-joined (one row per document). */}
                <TableHeaderColumn dataField="components" dataFormat={textFmt} width="160">
                  COMPONENTS
                </TableHeaderColumn>
                <TableHeaderColumn dataField="total_qty" dataAlign="right" dataFormat={qtyCell} width="100">
                  QTY
                </TableHeaderColumn>
                <TableHeaderColumn dataField="service_amount" dataAlign="right" dataFormat={amountFmt} width="130">
                  AMOUNT
                </TableHeaderColumn>
                <TableHeaderColumn dataField="status" dataFormat={textFmt} width="120">
                  STATUS
                </TableHeaderColumn>
                <TableHeaderColumn
                  dataField="id"
                  isKey
                  dataAlign="center"
                  dataFormat={actionsFmt}
                  width="270"
                >
                  ACTIONS
                </TableHeaderColumn>
              </BootstrapTable>
            </>
          )}
        </div>
        <AttachmentViewModal
          show={showAttachmentModal}
          onHide={() => {
            setShowAttachmentModal(false);
            setSelectedRowDetails(null);
          }}
          row={selectedRowDetails}
          moduleName={"Outside Services Transfer"}
          fieldMap={{
            docNo: "doc_no",
            date: "doc_date",
            type: "supplier_name",
            attachment: "attachment_file",
          }}
        />
      </div>
    </div>
  );
}
